import {Component, OnDestroy, OnInit} from '@angular/core';
import {TitlesFacade} from '@kion/data-access/data-access-titles';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {Address, Auth, Basket, Title} from '@kion/data-models';
import {AddressFacade} from '@kion/data-access/data-access-address';
import {BasketsFacade} from '@kion/data-access/data-access-baskets';
import {CmsConfig} from '../../config/cms.config';
import {AuthsFacade} from '@kion/data-access/data-access-auths';
import {CorrelationObserverService} from '@kion/ngrx-helper';
import {ToastService} from '@kion/ui';
import {SubSink} from 'subsink';
import {TranslateService} from '@ngx-translate/core';
import {Router} from "@angular/router";

@Component({
  selector: 'kion-cms-checkout-address',
  templateUrl: './checkout-address.component.html',
  styleUrls: ['./checkout-address.component.scss']
})
export class CheckoutAddressComponent implements OnInit, OnDestroy {

  titles$: Observable<{ key: string, value: string }[]>;
  basket$: Observable<Basket>;
  invoiceAddress$: Observable<Address>;
  deliveryAddress$: Observable<Address>;


  deliveryAddressSameAsInvoice: boolean;
  newDeliveryAddress: boolean;
  newInvoiceAddress: boolean;

  backLink: string;
  forwardLink: string;

  isoCode: string;

  addresses$: Observable<Address[]>;
  addressEditInvoice$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  addressEditDelivery$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  isUpdating$ = new BehaviorSubject<boolean>(false)
  private subSink: SubSink;

  constructor(
    private titlesFacade: TitlesFacade,
    private addressFacade: AddressFacade,
    private basketsFacade: BasketsFacade,
    private cmsConfig: CmsConfig,
    private authsFacade: AuthsFacade,
    private observer: CorrelationObserverService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private router: Router
  ) {
    this.backLink = '/' + this.cmsConfig.routes?.checkoutBaseRoute;
    this.forwardLink = '/' + this.backLink + '/' + this.cmsConfig.routes?.checkoutRoutes?.delivery;
    this.isoCode = this.cmsConfig.isoCode;
    this.subSink = new SubSink();
  }

  get isUser$(): Observable<boolean> {
    return this.authsFacade.all$.pipe(
      map(
        (auths: Auth[]) => {
          const auth = auths[0];
          return !!auth.user;
        }
      )
    )
  }

  get isSameAsInvoice$(): Observable<boolean> {
    return combineLatest(
      [
        this.invoiceAddress$,
        this.deliveryAddress$,
        this.isUser$
      ]
    ).pipe(
      map(
        ([invoice, deliver, isUser]) => {
          if (isUser) {
            if (invoice && deliver) {
              return invoice.id === deliver.id;
            }
            return false;
          }
          if (invoice && deliver) {
            return invoice.formattedAddress === deliver.formattedAddress;
          }
          return false
        }
      )
    )
  }

  get canProceed$(): Observable<boolean> {
    return this.basket$.pipe(
      map(
        (basket: Basket) => {
          return !!(basket?.paymentAddress?.id && basket?.deliveryAddress?.id);
        }
      )
    );
  }

  ngOnInit(): void {
    this.addressFacade.loadAll();
    this.addresses$ = this.addressFacade.all$;
    this.deliveryAddressSameAsInvoice = true;
    this.newInvoiceAddress = true;
    this.basketsFacade.createOrFindBasket();
    this.titlesFacade.loadAll();
    this.basket$ = this.basketsFacade.all$.pipe(
      map(
        (baskets: Basket[]) => baskets.find(
          (basket: Basket) => basket.id === window.localStorage.getItem('basketId')
        )
      )
    );
    this.titles$ = this.titlesFacade.all$.pipe(
      map(
        (titles: Title[]) => titles.map(
          (title: Title) => {
            return {
              key: title.code,
              value: title.name
            }
          }
        )
      )
    );
    this.invoiceAddress$ = this.basket$.pipe(
      map(
        (basket: Basket) => basket?.paymentAddress
      )
    );
    this.deliveryAddress$ = this.basket$.pipe(
      map(
        (basket: Basket) => basket?.deliveryAddress
      )
    );

    this.subSink.sink = combineLatest(
      [
        this.invoiceAddress$,
        this.authsFacade.all$
      ]
    ).pipe(take(2)).subscribe(
      ([address, auths]) => {
        const auth = auths[0];
        if (address && auth.user) {
          this.newInvoiceAddress = false;
        }
      }
    );
    this.subSink.sink = combineLatest(
      [
        this.deliveryAddress$,
        this.authsFacade.all$,
        this.isUser$,
      ]
    ).pipe(take(3)).subscribe(
      ([address, auths, isUser]) => {
        const auth = auths[0];
        if (address && (auth.user || !!!isUser)) {
          this.deliveryAddressSameAsInvoice = false;
        }
      }
    );

    this.subSink.sink = combineLatest(
      [
        this.isUser$,
        this.invoiceAddress$,
        this.deliveryAddress$,
        this.basket$,
        this.addresses$
      ]
    ).pipe(
      map(
        ([isUser, invoiceAddress, deliveryAddress, basket, addresses]) => {
          if (isUser && basket && addresses) {
            if (!invoiceAddress && !deliveryAddress) {
              return addresses[0]
            }
          }
          return null
        }
      )
    ).subscribe(
      (address) => {
        if (address) {
          this.selectInvoiceAddress(address.id);
          this.selectDeliveryAddress(address.id)
        }
      }
    );
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  upsertInvoiceAddress(address: Partial<Address>): void {
    this.subSink.sink = this.basket$.pipe(take(1)).subscribe(
      (basket: Basket) => {
        this.subSink.sink = this.authsFacade.all$.pipe(take(1)).subscribe(
          (auths: Auth[]) => {
            const auth = auths[0];
            if (!auth.user && basket.user.uid === 'anonymous') {
              this.subSink.sink = this.observer.subscribeToAction(
                this.basketsFacade.addEmailToBasket(basket.id, address.email),
                () => {
                  this.subSink.sink = this.observer.subscribeToAction(
                    this.addressFacade.createAddress({
                      ...address,
                      country: {isocode: this.cmsConfig.isoCode, name: ""}
                    }, basket.id, true),
                    () => {
                      if (this.deliveryAddressSameAsInvoice) {
                        this.upsertDeliveryAddress(address);
                      }
                    },
                    () => {
                      this.toastService.show(
                        {
                          text: this.translateService.instant('toast.checkout.address.invoice.save.error'),
                          type: 'error'
                        }
                      );
                    }
                  );
                },
                () => {
                  this.toastService.show(
                    {
                      text: this.translateService.instant('toast.checkout.user.save.error'),
                      type: 'error'
                    }
                  );
                }
              );
              return;
            }
            if (!auth.user) {
              this.isUpdating$.next(true);
              this.subSink.sink = this.observer.subscribeToAction(
                this.addressFacade.createAddress({
                  ...address,
                  country: {isocode: this.cmsConfig.isoCode, name: ""}
                }, basket.id, true),
                () => {
                  this.isUpdating$.next(false);
                  if (this.deliveryAddressSameAsInvoice) {
                    this.upsertDeliveryAddress(address);
                  }
                },
                () => {
                  this.isUpdating$.next(false);
                  this.toastService.show(
                    {
                      text: this.translateService.instant('toast.checkout.address.invoice.save.error'),
                      type: 'error'
                    }
                  );
                }
              );
              return;
            }
            if (address.id) {
              this.subSink.sink = this.observer.subscribeToAction(
                this.addressFacade.updateAddress(address.id, {
                  ...address,
                  country: {isocode: this.cmsConfig.isoCode, name: ""}
                }, basket.id),
                () => {
                  if (this.deliveryAddressSameAsInvoice) {
                    this.upsertDeliveryAddress(address);
                  }
                },
                () => {
                  this.toastService.show(
                    {
                      text: this.translateService.instant('toast.checkout.address.invoice.save.error'),
                      type: 'error'
                    }
                  );
                }
              );
              return;
            }
            this.subSink.sink = this.observer.subscribeToAction(
              this.addressFacade.createAddress({
                ...address,
                country: {isocode: this.cmsConfig.isoCode, name: ""}
              }, basket.id, true),
              () => {
                this.subSink.sink = this.addressFacade.lastCreatedId$.pipe(take(1)).subscribe(
                  id => {
                    this.selectInvoiceAddress(id);
                    this.setEditMode(id, true)
                  }
                );
                if (this.deliveryAddressSameAsInvoice) {
                  this.upsertDeliveryAddress(address);
                }
              },
              () => {
                this.toastService.show(
                  {
                    text: this.translateService.instant('toast.checkout.address.invoice.save.error'),
                    type: 'error'
                  }
                );
              }
            );
          }
        );
      }
    )
  }

  upsertDeliveryAddress(address: Partial<Address>): void {
    this.subSink.sink = this.basket$.pipe(take(1)).subscribe(
      (basket: Basket) => {
        this.subSink.sink = this.authsFacade.all$.pipe(take(1)).subscribe(
          (auths: Auth[]) => {
            const auth = auths[0];
            if (!auth.user) {
              this.isUpdating$.next(true);
              setTimeout(() => {
                this.subSink.sink = this.observer.subscribeToAction(
                  this.addressFacade.createAddress({
                    ...address,
                    country: {isocode: this.cmsConfig.isoCode, name: ""}
                  }, basket.id, false),
                  () => {
                    this.isUpdating$.next(false);
                  },
                  () => {
                    this.isUpdating$.next(false);
                    this.toastService.show(
                      {
                        text: this.translateService.instant('toast.checkout.address.delivery.save.error'),
                        type: 'error'
                      }
                    );
                  }
                );
              }, 500);
              return;
            }
            if (address.id) {
              this.subSink.sink = this.observer.subscribeToAction(
                this.addressFacade.updateAddress(address.id, {
                  ...address,
                  country: {isocode: this.cmsConfig.isoCode, name: ""}
                }, basket.id),
                () => {
                },
                () => {
                  this.toastService.show(
                    {
                      text: this.translateService.instant('toast.checkout.address.delivery.save.error'),
                      type: 'error'
                    }
                  );
                }
              );
              return;
            }
            this.subSink.sink = this.observer.subscribeToAction(
              this.addressFacade.createAddress({
                ...address,
                country: {isocode: this.cmsConfig.isoCode, name: ""}
              }, basket.id, false),
              () => {
                this.subSink.sink = this.addressFacade.lastCreatedId$.pipe(take(1)).subscribe(
                  id => {
                    this.selectDeliveryAddress(id);
                    this.setEditMode(id, false);
                  }
                );
              },
              () => {
                this.toastService.show(
                  {
                    text: this.translateService.instant('toast.checkout.address.delivery.save.error'),
                    type: 'error'
                  }
                );
              }
            );
          }
        );
      }
    )
  }

  markAsSameAsInvoiceAddress(): void {
    this.deliveryAddressSameAsInvoice = true;
    this.newDeliveryAddress = false;
    this.subSink.sink = this.invoiceAddress$.pipe(take(1)).subscribe(
      address => {
        if (address) {
          if (address.id) {
            this.selectDeliveryAddress(address.id);
            this.deliveryAddressSameAsInvoice = true;
          }
        }
      }
    )
  }

  createNewDeliveryAddress(): void {
    this.deliveryAddressSameAsInvoice = false;
    this.newDeliveryAddress = true;
  }

  markAsNewInvoiceAddress(): void {
    this.newInvoiceAddress = true;
  }

  selectInvoiceAddress(id: string): void {
    this.newInvoiceAddress = false;
    this.subSink.sink = this.basket$.pipe(take(1)).subscribe(
      basket => {
        this.basketsFacade.setAddress(basket.id, id, false);
        if (this.deliveryAddressSameAsInvoice) {
          this.basketsFacade.setAddress(basket.id, id, true);
        }
      }
    );
  }

  selectDeliveryAddress(id: string): void {
    console.log('selected address id:', id)
    this.deliveryAddressSameAsInvoice = false;
    this.newDeliveryAddress = false;
    this.subSink.sink = this.basket$.pipe(take(1)).subscribe(
      basket => {
        this.basketsFacade.setAddress(basket.id, id, true);
      }
    );
  }

  updateAddress(address: Partial<Address>): void {
    this.isUpdating$.next(true);
    this.observer.subscribeToAction(
      this.addressFacade.updateAddress(address.id, address),
      () => {
        this.isUpdating$.next(false);
      },
      () => {
        this.isUpdating$.next(false);
      }
    )
  }

  setEditMode(id: string, isInvoice: boolean): void {
    if (isInvoice) {
      this.addressEditInvoice$.next(id);
      return;
    }
    this.addressEditDelivery$.next(id);
  }

  removeEditMode(isInvoice: boolean): void {
    if (isInvoice) {
      this.addressEditInvoice$.next('');
      return;
    }
    this.addressEditDelivery$.next('');
  }

  nextClicked() {
    // get current basketId
    this.subSink.sink = this.basket$.pipe(take(1)).subscribe(
      basket => {
        // user observer and subscribe to action
        this.subSink.sink = this.observer.subscribeToAction(
          this.basketsFacade.applyPostalCodeMapping(basket.id),
          () => {
            // on success route to forward link
            console.log('on success is called');
            this.router.navigateByUrl(this.forwardLink);
          },
          () => {
            console.log('on error is called');
            // on error show toast
            this.toastService.show(
              {
                text: this.translateService.instant('toast.checkout.postalcodemapping.error'),
                type: 'error'
              }
            );
          }
        )
      }
    );
  }
}
